import { Component, OnInit, Inject } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import * as FileSaver from "file-saver";

import { AuthService } from "../../auth/auth.service";
import { EmailService } from "../shared/email.service";
import { DocumentService } from "../shared/document.service";
import { SnackBarService } from "../../core/snack-bar.service";
import { ValidationHelperService } from "../../core/validation-helper.service";

import { Email } from "../shared/email.model";
import { Enquiry } from "../shared/enquiry.model";
import { FileDto } from "../../shared/file-dto.model";
import { QuoteDocGenData } from "../shared/quote-doc-gen-data.model";
import { RepaymentOption } from "../shared/repayment-option.model";

@Component({
    template: require('./email-enquiry-dialog.component.html'),
    styles: [require('./email-enquiry-dialog.component.css')]
})
export class EmailEnquiryDialogComponent implements OnInit {
    applicationFormAvailable: boolean;
    emailForm: FormGroup;
    enquiry: Enquiry;
    fileInputFC: FormControl;
    repaymentOptions: RepaymentOption[];
    showSpinner: boolean;

    constructor(
        private authService: AuthService,
        private documentService: DocumentService,
        private emailService: EmailService,
        private fb: FormBuilder,
        private snackBarService: SnackBarService,
        private validationHelperService: ValidationHelperService,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<EmailEnquiryDialogComponent>) {

        this.fileInputFC = new FormControl();
        this.emailForm = this.fb.group({
            showAssetCost: [true, Validators.required],
            repaymentOptionCheckboxes: this.fb.array([]),

            to: ['', [Validators.required, Validators.maxLength(300)]],
            cc: ['', Validators.maxLength(300)],
            bcc: ['', Validators.maxLength(300)],
            subject: ['Enquiry', Validators.maxLength(300)],
            body: ['', Validators.required],

            attachments: this.fb.array([])
        });
    }

    ngOnInit() {
        this.applicationFormAvailable = this.data.applicationFormAvailable as boolean;
        this.enquiry = this.data.enquiry as Enquiry;
        this.repaymentOptions = this.data.repaymentOptions as RepaymentOption[];
        const selectedRepaymentIndex = this.data.selectedRepaymentIndex as number;

        this.repaymentOptions.forEach((opt, index) => {
            this.repaymentCheckboxesFA.push(new FormControl(index === selectedRepaymentIndex));
        });

        this.prefillEmail();
    }

    get repaymentCheckboxesFA(): FormArray { return this.emailForm.get("repaymentOptionCheckboxes") as FormArray; }
    get attachmentsFA(): FormArray { return this.emailForm.get("attachments") as FormArray; }

    prefillEmail(): void {
        const profile = this.authService.userProfile;

        this.emailForm.get("to").setValue(this.enquiry.emailAddress);
        this.emailForm.get("bcc").setValue(profile.emailAddress);

        let signature = profile.fullName;
        if (profile.jobTitle) { signature = signature.concat("\n", profile.jobTitle); }
        if (profile.phoneNumber) { signature = signature.concat("\nPh. ", profile.phoneNumber); }
        if (profile.mobileNumber) { signature = signature.concat("\nMob. ", profile.mobileNumber); }
        if (profile.emailAddress) { signature = signature.concat("\n", profile.emailAddress); }

        let body = `Dear ${this.enquiry.contactName},\n\n\n\n${signature}`
        this.emailForm.get("body").setValue(body);
    }

    displayPaymentFrequency(index: number): boolean {
        return (index == 0)
            || (this.repaymentOptions[index - 1].paymentFrequencyID != this.repaymentOptions[index].paymentFrequencyID)
    }

    downloadApplicationForm(): void {
        this.showSpinner = true;

        this.documentService.downloadApplicationForm(this.enquiry.id)
            .subscribe(
            res => {
                this.showSpinner = false;
                var fileName = res.headers.get("Content-Disposition").match(/"(.*?)"/g).toString(); // get the string between the quotes, but includes the quotes
                fileName = fileName.replace(/["]+/g, ''); // remove the quotes

                FileSaver.saveAs(res.body, fileName);
            },
            err => {
                this.showSpinner = false;
                this.snackBarService.openSnackBar(err as string)
            });
    }

    downloadQuote(): void {
        const checkboxValues: boolean[] = this.repaymentCheckboxesFA.value;
        if (!checkboxValues.some(checked => checked === true)) {
            this.snackBarService.openSnackBar("Please select at least one repayment option.");
            return;
        }

        this.showSpinner = true;
        const showAssetCost = this.emailForm.get('showAssetCost').value as boolean;
        const quoteDocGenData = this.documentService.prepareQuoteDocGenData(showAssetCost, checkboxValues, this.repaymentOptions);

        this.documentService.downloadQuotePdf(this.enquiry.id, quoteDocGenData)
            .subscribe(
            res => {
                this.showSpinner = false;
                var fileName = res.headers.get("Content-Disposition").match(/"(.*?)"/g).toString(); // get the string between the quotes, but includes the quotes
                fileName = fileName.replace(/["]+/g, ''); // remove the quotes

                FileSaver.saveAs(res.body, fileName);
            },
            err => {
                this.showSpinner = false;
                this.snackBarService.openSnackBar(err as string)
            });
    }

    // refer to docs for File obj structure https://developer.mozilla.org/en-US/docs/Web/API/File
    // code modified from https://nehalist.io/uploading-files-in-angular2/
    onFileChange(event: any) {
        let reader = new FileReader();
        if (event.target.files && event.target.files.length > 0) {
            let file = event.target.files[0];
            reader.readAsDataURL(file); // see https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
            reader.onload = () => {

                let attachmentFG: FormGroup = this.fb.group({
                    fileName: file.name,
                    fileType: file.type,
                    // readAsDataURL() returns a string in the form "data:application/pdf;base64,JVBERi0x..."
                    fileContentBase64: (reader.result as string).split(',')[1]
                });

                this.attachmentsFA.push(attachmentFG);
                this.fileInputFC.reset();
            };
        }
    }

    prepareSendEmail(): Email {
        const formModel = this.emailForm.value;
        const checkboxValues: boolean[] = this.repaymentCheckboxesFA.value;

        const showAssetCost = this.emailForm.get("showAssetCost").value as boolean;
        const docGenData = this.documentService.prepareQuoteDocGenData(showAssetCost, checkboxValues, this.repaymentOptions);

        // shallow copy.. deep copy shouldnt be necessary in this instance since attachment files aren't saved
        const attachFiles = this.attachmentsFA.value as FileDto[] 

        const emailToSend: Email = {
            quoteDocGenData: docGenData,
            to: formModel.to as string,
            cc: formModel.cc as string,
            bcc: formModel.bcc as string,
            subject: formModel.subject as string,
            body: formModel.body as string,

            attachments: attachFiles
        };

        return emailToSend;
    }

    removeAttachment(index: number): void {
        this.attachmentsFA.removeAt(index);
    }

    sendEmail() {
        const checkboxValues: boolean[] = this.repaymentCheckboxesFA.value;
        if (!checkboxValues.some(checked => checked === true) || this.emailForm.invalid) {
            this.validationHelperService.markAllTouched(this.emailForm);
            this.snackBarService.openSnackBar("Please select at least one repayment option and fill in required values.");
            return;
        }

        this.showSpinner = true;
        const email = this.prepareSendEmail();

        this.emailService.sendEmail(this.enquiry.id, email)
            .then(() => {
                this.showSpinner = false;
                this.snackBarService.openSnackBar("Email sent.");
                this.dialogRef.close();
            })
            .catch(err => {
                this.showSpinner = false;
                this.snackBarService.openSnackBar(err);
            });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/enquiry/email-enquiry-dialog/email-enquiry-dialog.component.ts